import { c } from 'ttag';

import { PermissionDomain } from '../../graphql/__generated__/schema';
import { PortalModulesQuery } from '../../graphql/__generated__/types';

export type PortalModuleRef = NonNullable<PortalModulesQuery['portalModules']>[number];

/** What classing a right needs: both screens of this folder select these two fields. */
export interface ClassifiableRight {
    module?: string | null;
    domain?: PermissionDomain | null;
}

export interface RightsGroup<R extends ClassifiableRight> {
    /** Stable React key: the module code, or a sentinel for the two module-less families. */
    key: string;
    label: string;
    /**
     * Whether the module's screen exists. `undefined` on the groups that hang off no
     * module: « livré » says nothing about administration.
     */
    delivered?: boolean;
    /** The group exists because rights did not fit the referential. Never true today. */
    anomaly?: boolean;
    permissions: R[];
}

const ADMIN_KEY = '__admin__';
const UNCLASSED_KEY = '__unclassed__';

/**
 * The rights referential, classed **by module**: the axis of the back-office mockup,
 * and the one that survives the referential going from 13 rights to 36. Grouping by
 * capability put 24 of those 36 under « Consulter et partager » alone, which is a list
 * one scrolls rather than reads.
 *
 * **Attention, the label and the order come from `portalModules`, never from this file.** The
 * module referential is the API's, it is what the portal menu is drawn from, and a
 * second copy of it here is exactly what the query exists to avoid. What the front does
 * write is the name of the module-less family, for want of an API entry to read it from.
 *
 * The order is the menu's (`sortOrder`), then the anomalies, then administration last:
 * it is the only family hanging off no module, and the smallest audience.
 *
 * Module groups are returned **even when empty**: the module referential is rendered
 * whole, a module granting nothing yet being a fact worth showing rather than a row to
 * drop. The two module-less families are returned only when something falls into them.
 * A caller summarising « which families does this role touch » filters the empties.
 */
export const buildRightsGroups = <R extends ClassifiableRight>(
    rights: readonly R[],
    modules: readonly PortalModuleRef[]
): RightsGroup<R>[] => {
    const inMenuOrder = [...modules].sort((left, right) => left.sortOrder - right.sortOrder);
    const named = new Set(inMenuOrder.map((module) => module.code));

    const groups: RightsGroup<R>[] = inMenuOrder.map((module) => ({
        key: module.code,
        label: module.label,
        delivered: module.delivered,
        permissions: rights.filter((right) => right.module === module.code),
    }));

    // A right pointing at a module the referential does not name. There is none today,
    // and that is precisely why it needs a group: a right silently dropped for want of a
    // heading is a hole nobody can see. Sorted by code so the order stays stable.
    const orphanCodes = [
        ...new Set(
            rights
                .map((right) => right.module)
                .filter((code): code is string => Boolean(code) && !named.has(code as string))
        ),
    ].sort();
    for (const code of orphanCodes) {
        groups.push({
            key: code,
            label: c('Rights matrix').t`Unknown module (${code})`,
            anomaly: true,
            permissions: rights.filter((right) => right.module === code),
        });
    }

    // The module-less family. The API states the five administration rights are the only
    // ones carrying no module, so `domain` is read to *name* the family rather than to
    // form it, and a module-less right that is not an administration one lands in the
    // anomaly group below instead of being quietly filed under a heading that lies.
    const moduleLess = rights.filter((right) => !right.module);
    const unclassed = moduleLess.filter((right) => right.domain !== PermissionDomain.Admin);
    const administration = moduleLess.filter((right) => right.domain === PermissionDomain.Admin);

    if (unclassed.length > 0) {
        groups.push({
            key: UNCLASSED_KEY,
            label: c('Rights matrix').t`Rights outside the referential`,
            anomaly: true,
            permissions: unclassed,
        });
    }

    if (administration.length > 0) {
        groups.push({
            key: ADMIN_KEY,
            label: c('Rights matrix').t`Administration`,
            permissions: administration,
        });
    }

    return groups;
};
